import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { listarLog } from '../api/log';
import type { EventoLog } from '../api/log';

const INTERVALO_MS = 5000;
const MAXIMO_DE_LINHAS = 1000;

const doisDigitos = (n: number) => String(n).padStart(2, '0');
const hora = (valor: string) => {
    const d = new Date(valor);
    return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)} ${d.toLocaleTimeString('pt-BR')}`;
};

type Conexao = 'ao-vivo' | 'pausado' | 'sem-conexao';

export function LogPainel() {
    const [linhas, setLinhas] = useState<EventoLog[]>([]);
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useState('');
    const [pausado, setPausado] = useState(false);
    const [conexao, setConexao] = useState<Conexao>('ao-vivo');
    const [ultimaConsulta, setUltimaConsulta] = useState<Date | null>(null);
    const cursor = useRef<string | null>(null);
    const vistos = useRef(new Set<string>());
    const tela = useRef<HTMLDivElement>(null);
    const colado = useRef(true);

    const consultar = useCallback(async (reiniciar: boolean) => {
        if (reiniciar) {
            cursor.current = null;
            vistos.current = new Set();
            setLinhas([]);
        }
        try {
            const pagina = await listarLog({ desde: cursor.current, turmaId: turmaId || null });
            const novos = pagina.eventos.filter((evento) => !vistos.current.has(evento.id));
            novos.forEach((evento) => vistos.current.add(evento.id));
            if (pagina.ate) cursor.current = pagina.ate;
            if (novos.length) setLinhas((atuais) => [...atuais, ...novos].slice(-MAXIMO_DE_LINHAS));
            setConexao('ao-vivo');
        } catch {
            setConexao('sem-conexao');
        }
        setUltimaConsulta(new Date());
    }, [turmaId]);

    useEffect(() => {
        listarTurmas().then(setTurmas).catch(() => setTurmas([]));
    }, []);

    // Troca de turma recomeça a tela do zero.
    useEffect(() => {
        void consultar(true);
    }, [consultar]);

    useEffect(() => {
        if (pausado) return undefined;
        const relogio = window.setInterval(() => void consultar(false), INTERVALO_MS);
        return () => window.clearInterval(relogio);
    }, [pausado, consultar]);

    // Acompanha o fim do terminal, a menos que o usuário tenha rolado para cima para ler.
    useLayoutEffect(() => {
        const elemento = tela.current;
        if (elemento && colado.current) elemento.scrollTop = elemento.scrollHeight;
    }, [linhas]);

    const status = pausado
        ? 'pausado'
        : conexao === 'sem-conexao'
          ? 'sem conexão · tentando de novo em 5s'
          : `ao vivo · atualiza a cada 5s${ultimaConsulta ? ` · última consulta ${ultimaConsulta.toLocaleTimeString('pt-BR')}` : ''}`;

    return (
        <div className="log">
            <div className="terminal">
                <div className="terminal-barra">
                    <span className={`terminal-luz terminal-luz-${pausado ? 'pausado' : conexao}`} aria-hidden="true" />
                    <span className="terminal-status" role="status">{status}</span>
                    <label className="terminal-filtro">
                        turma
                        <select value={turmaId} onChange={(e) => setTurmaId(e.target.value)}>
                            <option value="">todas</option>
                            {turmas.map((turma) => (
                                <option key={turma.id} value={turma.id}>{turma.id}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" className="terminal-botao" onClick={() => setPausado((atual) => !atual)}>
                        {pausado ? 'Retomar' : 'Pausar'}
                    </button>
                    <button type="button" className="terminal-botao" onClick={() => setLinhas([])}>
                        Limpar
                    </button>
                </div>
                <div
                    ref={tela}
                    className="terminal-tela"
                    role="log"
                    aria-live="polite"
                    aria-label="Log de uso da plataforma"
                    tabIndex={0}
                    onScroll={(e) => {
                        const alvo = e.currentTarget;
                        colado.current = alvo.scrollHeight - alvo.scrollTop - alvo.clientHeight < 24;
                    }}
                >
                    {linhas.length === 0 && <p className="terminal-vazio">$ aguardando eventos…</p>}
                    {linhas.map((evento) => (
                        <p key={evento.id} className={`terminal-linha nivel-${evento.nivel}`}>
                            <span className="terminal-hora">{hora(evento.em)}</span>
                            <span className="terminal-nivel">{evento.nivel.toUpperCase()}</span>
                            <span className="terminal-fonte">{evento.fonte}</span>
                            <span className="terminal-turma">{evento.turma_id ?? '—'}</span>
                            <span className="terminal-mensagem">{evento.mensagem}</span>
                        </p>
                    ))}
                </div>
            </div>
        </div>
    );
}
